import { useEffect, useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';

import { auth, functions } from '../lib/firebase';

export type EntitlementMap = ReadonlyMap<string, true>;

interface GetEntitlementsResponse {
  roles: string[];
}

const createEntitlementMap = (roles: string[]): EntitlementMap => {
  const mapEntries = roles.map((role) => [role, true] as const);
  return new Map(mapEntries);
};

const readRolesFromToken = async (): Promise<string[]> => {
  if (!auth.currentUser) {
    return [];
  }

  const { claims } = await auth.currentUser.getIdTokenResult(true);
  const claimRoles = claims?.entitlements;

  if (Array.isArray(claimRoles)) {
    return claimRoles.filter((value): value is string => typeof value === 'string');
  }

  return [];
};

const fetchRolesFromCallable = async (): Promise<string[]> => {
  const callable = httpsCallable<undefined, GetEntitlementsResponse>(functions, 'getUserEntitlements');
  const response = await callable();
  return Array.isArray(response.data.roles) ? response.data.roles : [];
};

const resolveEntitlements = async (): Promise<EntitlementMap> => {
  if (!auth.currentUser) {
    return new Map();
  }

  const tokenRoles = await readRolesFromToken();

  if (tokenRoles.length > 0) {
    return createEntitlementMap(tokenRoles);
  }

  const fallbackRoles = await fetchRolesFromCallable();
  return createEntitlementMap(fallbackRoles);
};

export function useEntitlements(): {
  entitlements: EntitlementMap;
  isLoading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
} {
  const [entitlements, setEntitlements] = useState<EntitlementMap>(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(undefined);

  const loadEntitlements = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);

    try {
      const entitlementMap = await resolveEntitlements();
      setEntitlements(entitlementMap);
    } catch (fetchError) {
      setError(fetchError);
      setEntitlements(new Map());
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadEntitlements();
  }, [loadEntitlements]);

  return {
    entitlements,
    isLoading,
    error,
    refetch: loadEntitlements,
  };
}

